// src/Navbar.tsx

import React, { useState, useEffect } from 'react'; 
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ItemCarrito } from './types';

interface NavbarProps {
    totalItems: number;
    onWhatsAppClick: () => void; 
    carrito: ItemCarrito[]; 
    onRemoveFromCart: (id: number) => void; 
}

const IconoWhatsappSVG: React.FC = () => (
    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C7.82 21 3 16.18 3 10V5z"></path></svg>
);

const IconoCarritoSVG: React.FC = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
);

const IconoCerrarSVG: React.FC = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
);

const Navbar: React.FC<NavbarProps> = ({ 
    totalItems, 
    onWhatsAppClick, 
    carrito, 
    onRemoveFromCart 
}) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isCartOpen, setIsCartOpen] = useState(false);
    
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            const scrolled = window.scrollY > 50;
            setIsScrolled(scrolled);
            
            if (scrolled && isMobileMenuOpen) setIsMobileMenuOpen(false);
            if (scrolled && isCartOpen) setIsCartOpen(false);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isMobileMenuOpen, isCartOpen]); 

    const closeMenus = () => {
        setIsMobileMenuOpen(false);
        setIsCartOpen(false);
    };

    const getLinkClasses = (path: string) => {
        const isActive = location.pathname === path;
        const baseClasses = "text-md font-semibold px-3 py-2 rounded-lg transition duration-150 ease-in-out block w-full text-left md:w-auto md:text-center tracking-wide";
        const activeClasses = isActive 
            ? "text-yellow-500 bg-gray-900/50" 
            : "text-gray-300 hover:text-yellow-500 hover:bg-gray-800/50";
        return `${baseClasses} ${activeClasses}`;
    };

    const rutasOscuras = ['/catalogo', '/concesionarios', '/nosotros', '/carrito'];
    const esRutaOscura = rutasOscuras.includes(location.pathname) || location.pathname.startsWith('/detalle');
    
    const showDarkBackground = isScrolled || esRutaOscura;

    const navBackgroundClass = showDarkBackground
        ? 'bg-black/95 backdrop-blur-md shadow-lg border-b border-gray-800' 
        : 'bg-transparent';

    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBackgroundClass}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-20">
                    
                    {/* LOGO MEJORADO: BESTUNE | CARS */}
                    <div className="flex-shrink-0">
                        <Link to="/" onClick={closeMenus} className="flex items-center group">
                            <img src="/LogoBestune.webp" alt="Bestune Logo" className="h-8 md:h-10 w-auto" />
                            
                            {/* === CAMBIO 1: Separador Vertical visible en móvil === */}
                            {/* Se eliminó 'hidden sm:block' y se ajustó la altura/margen para móvil */}
                            <div className="h-6 md:h-8 w-px bg-gray-600 mx-2 md:mx-3"></div>
                            
                            {/* === CAMBIO 2: Texto "CARS" visible en móvil === */}
                            {/* Se eliminó 'hidden sm:block' y se usó text-lg para móvil, text-2xl para PC */}
                            <span className="text-white font-bold text-lg md:text-2xl tracking-[0.15em] md:tracking-[0.2em] group-hover:text-yellow-500 transition-colors font-sans pt-1">
                                CARS
                            </span>
                        </Link>
                    </div>

                    <div className="hidden md:flex md:items-center md:space-x-4">
                        <Link to="/" className={getLinkClasses('/')}>Inicio</Link>
                        <Link to="/catalogo" className={getLinkClasses('/catalogo')}>Catálogo</Link>
                        <Link to="/concesionarios" className={getLinkClasses('/concesionarios')}>Concesionarios</Link>
                        <Link to="/nosotros" className={getLinkClasses('/nosotros')}>Nosotros</Link>
                        <Link to="/contacto" className={getLinkClasses('/contacto')}>Contacto</Link>
                    </div>

                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onWhatsAppClick}
                            className="hidden md:flex items-center bg-green-600 text-white font-bold py-2 px-4 rounded-full hover:bg-green-700 transition duration-150 text-sm shadow-md"
                        >
                            <IconoWhatsappSVG />
                            Contacto
                        </button>

                        <div className="relative">
                            <button
                                onClick={() => setIsCartOpen(!isCartOpen)}
                                className="relative p-2 rounded-full text-white hover:text-yellow-500 hover:bg-gray-800 transition duration-150"
                                aria-label="Abrir Carrito"
                            >
                                <IconoCarritoSVG />
                                {totalItems > 0 && (
                                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-yellow-600 rounded-full">
                                        {totalItems}
                                    </span>
                                )}
                            </button>
                            
                            {isCartOpen && (
                                <div 
                                    className="absolute right-0 mt-3 w-80 bg-white border border-gray-700 rounded-lg shadow-xl py-3 z-50 animate-fadeInUp"
                                    onClick={(e) => e.stopPropagation()} 
                                >
                                    <div className="px-4 pb-2 border-b border-gray-800">
                                        <h3 className="text-lg font-bold text-black">
                                            {totalItems > 0 ? `Carrito (${totalItems})` : 'Carrito Vacío'}
                                        </h3>
                                    </div>
                                    
                                    <div className="max-h-60 overflow-y-auto">
                                        {totalItems > 0 ? (
                                            carrito.map(item => (
                                                <div key={item.id} className="flex items-center justify-between px-4 py-2 hover:bg-gray-200 transition duration-150 border-b border-gray-100">
                                                    
                                                    <div className="flex-shrink-0 mr-3">
                                                        <img 
                                                            src={item.imgUrl} 
                                                            alt={item.nombre} 
                                                            className="w-10 h-10 object-cover rounded-md border border-gray-300" 
                                                        />
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-800 truncate">{item.nombre} <span className="text-yellow-600">({item.cantidad})</span></p>
                                                        <Link 
                                                            to={`/detalle/${item.id}`}
                                                            className="text-xs text-yellow-600 cursor-pointer hover:underline transition"
                                                            onClick={closeMenus}
                                                        >
                                                            Ver Detalle
                                                        </Link>
                                                    </div>
                                                    <div className="flex flex-col items-end space-y-1">
                                                        <span className="text-sm font-bold text-black">${(item.precio * item.cantidad).toLocaleString('es-ES')}</span>
                                                        <button 
                                                            onClick={() => onRemoveFromCart(item.id)}
                                                            className="text-gray-400 hover:text-red-500 transition duration-150"
                                                        >
                                                            <IconoCerrarSVG />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500 text-center py-6">Tu carrito está vacío.</p>
                                        )}
                                    </div>

                                    {totalItems > 0 && (
                                        <div className="px-4 pt-3 border-t border-gray-800">
                                            <button 
                                                onClick={() => {
                                                    navigate('/carrito');
                                                    closeMenus();
                                                }}
                                                className="w-full bg-yellow-600 text-white font-bold py-2 rounded-lg hover:bg-yellow-700 transition duration-150 text-sm shadow-md"
                                            >
                                                Ver Carrito
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* === CAMBIO 3: Icono de Menú Blanco (text-white) === */}
                        <button 
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-yellow-500 hover:bg-gray-700 md:hidden"
                        >
                            {isMobileMenuOpen ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path></svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className={`md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'} pb-3 bg-black/95 border-t border-gray-800`}>
                <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                    <Link to="/" onClick={closeMenus} className={getLinkClasses('/')}>Inicio</Link>
                    <Link to="/catalogo" onClick={closeMenus} className={getLinkClasses('/catalogo')}>Catálogo</Link>
                    <Link to="/concesionarios" onClick={closeMenus} className={getLinkClasses('/concesionarios')}>Concesionarios</Link>
                    <Link to="/nosotros" onClick={closeMenus} className={getLinkClasses('/nosotros')}>Nosotros</Link>
                    <Link to="/contacto" onClick={closeMenus} className={getLinkClasses('/contacto')}>Contacto</Link>
                    
                    <button
                        onClick={() => { onWhatsAppClick(); closeMenus(); }}
                        className="flex items-center w-full bg-green-600 text-white font-bold py-3 px-3 rounded-lg hover:bg-green-700 transition duration-150 text-sm shadow-md mt-4 justify-center"
                    >
                        <IconoWhatsappSVG />
                        Contacto WhatsApp
                    </button>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;